import { Box, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Stack, TextField, Typography, alpha } from "@mui/material";
import { UIcon } from "../common/ui.icon";
import { GREY, INFO_COLOR, MAIN_COLOR, MAIN_COLOR_ACTIVE, SECONDARY_COLOR, WARN_COLOR, WHITE } from "../style/theme";
import { UNIT_CATEGORIES, UNIT_XP_PERC, useUnit } from "./unit";
import { UDialgoCloseIcon, UDialog, UDialogClose, UDialogCloseOnCompleteButton, useDialog } from "../common/ui.dialog";
import { useContext, useEffect, useState } from "react";
import { gradient } from "../common/ui.gradient";
import { UButton, UListItemButton } from "../common/ui.buttons";
import { BATTLE_TRAITS, useTraits } from "./unit.traits";
import { CPayButton, RP_COLOR } from "./cr.ui.requisition";
import { WEAPON_MODIFICATIONS } from "./unit.wmod";
import { ULine, UMarginTop, URow } from "../common/ui.page";
import { useRelics } from "./unit.relics";
import { UnitComposition } from "./unit.composition";
import { CUnitEditor } from "./cr.ui.composition";
import { useExt } from "./ext";
import { CUnitListContext } from "./cr.ui.order";

export function CUnitCompText(props) {
    const { comp, ...rest } = props;

    var compAsText = ''
    comp.upgrades.forEach(u => {
        compAsText += '+ ' + u.name + '\r\n';
    })
    if (comp.models) {
        comp.models.forEach(m => {
            compAsText += '' + m.count + 'x ' + m.name + '\r\n';
            m.upgrades.forEach(u => {
                compAsText += '   + ' + u.name + '\r\n';
            })
        })
    }

    return <Box {...rest} sx={{ whiteSpace: 'pre-wrap', ...rest.sx }}>{compAsText}</Box>
}

const UnitGradeName = {
    'bready': 'Pronto alla battaglia',
    'blooded': 'Insanguinato',
    'battleh': 'Temprato dalla battaglia',
    'heroic': 'Eroico',
    'legendary': 'Leggendario',
    'epic': 'Epico',
}

export function CUnitGrade(props) {
    const { grade } = props;

    return <Box sx={{ paddingY: 3 }}>
        <Box sx={{
            fontSize: 10,
            background: grade == 'epic' ? '#559c85' : MAIN_COLOR,
            lineHeight: 1,
            padding: 1,
            whiteSpace: 'nowrap',
            paddingX: 3, paddingRight: 12, borderRadius: 10, fontWeight: 500, position: 'relative'
        }}>
            {UnitGradeName[grade]}
            <Box sx={{ lineHeight: 0, position: 'absolute', right: 0, top: -8, }}>
                <UIcon size={32} name={grade} />
            </Box>
        </Box>

    </Box>
}

export function DataBar(props) {
    const { name, value, color } = props;

    return <URow sx={{
        background: 'linear-gradient(90deg, ' + color + ' 0%, rgba(40,30,90,0) 100% )',
        padding: 1,
        paddingX: 2,
    }}>
        <Typography sx={{ fontSize: 12 }}>{name}</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{value}</Typography>
    </URow>
}

export function CUnitBox(props) {
    const { unit, percXp, sx, color1, color2, color3, Component, ...rest } = props;

    const xpBgcolor = color3 || MAIN_COLOR_ACTIVE
    const xpPerc = (percXp ? percXp : 0)
    const xpBg = 'linear-gradient(90deg, ' + alpha(xpBgcolor, 0.1) + ' 0%, ' + alpha(xpBgcolor, xpPerc / 100) + ' 100%)';
    const isNew = unit.xp == 0 && unit.played == 0;

    return <Component
        {...rest}
        sx={{
            backgroundColor: color2 || GREY[300],
            paddingRight: 0,
            borderLeft: '8px solid ' + (color1 || MAIN_COLOR),
            ":hover": { backgroundColor: color2 || GREY[300] },
            position: 'relative',
            marginBottom: 1,
            ...sx,
        }}>
        <Box sx={{
            lineHeight: 1,
            position: 'absolute',
            left: -16,
            top: 8,
        }}>
            {!isNew && <UIcon name={unit.grade} />}
        </Box>
        {props.children}
        <Box sx={{
            position: 'absolute',
            height: '2px',
            background: xpBg,
            bottom: 0,
            left: 0,
            right: 0,
            width: xpPerc + '%',
        }}></Box>
    </Component>
}

export function CUnitShowDetailsButton(props) {
    const unitBox = useContext(CUnitListContext)
    if (!unitBox) {
        return <></>
    }
    const selected = unitBox.showDetails;

    return <Typography sx={{ color: selected ? WHITE : MAIN_COLOR_ACTIVE, ...props.sx }} onClick={() => unitBox.setShowDetails(!unitBox.showDetails)}>Mostra dettagli</Typography>
}


export function CUnitBoxName(props) {
    const { unit, showDetails } = props;
    const unitBox = useContext(CUnitListContext)
    const name = unit.name || unit.unitType;
    const subName = Boolean(unit.name) && unit.unitType;


    return <Stack sx={{ flex: 1 }}>
        <ListItemText
            sx={{ margin: 0, }}
            primaryTypographyProps={{ sx: { fontWeight: 600 } }}
            primary={name}
            secondaryTypographyProps={{ sx: { fontWeight: 500, fontSize: 12, color: GREY[700], lineHeight: 1 } }}
            secondary={subName}
        />
        {((unitBox && unitBox.showDetails) || showDetails) && <CUnitBoxUpgrades unit={unit} />}
    </Stack>
}

function CUnitBoxUpgrades(props) {
    const { unit } = props;
    const ext = useExt();
    const percXp = UNIT_XP_PERC(unit.xp);

    return <Stack>
        {unit.xp > 0 && <Stack direction={"row"} alignItems="center">
            <Typography sx={{ fontSize: 10 }} color={INFO_COLOR}>XP {unit.xp}</Typography>
            <LinearProgress sx={{ flex: 1, marginX: 2 }} variant="determinate" color="info" value={percXp} />
        </Stack>}
        {unit.weaponModifications.length > 0 && unit.weaponModifications.map(mm => <Stack sx={{ marginTop: 2 }} key={mm.id}>
            <Typography sx={{
                fontSize: 12, color: MAIN_COLOR_ACTIVE
            }}>Arma modificata: {mm.weapon}</Typography>
            {mm.modifications.map(m => <ListItemText
                key={m.id}
                primary={m.name}
                primaryTypographyProps={{ sx: { fontSize: 10, marginLeft: 2, } }}
                secondary={m.description}
                secondaryTypographyProps={{ sx: { fontSize: 10, marginLeft: 2, } }}
            />)}

        </Stack>)}
        {unit.traits.length > 0 && <Stack sx={{ marginTop: 2 }}>
            <Typography sx={{
                fontSize: 12, color: RP_COLOR
            }}>Onori di Battaglia</Typography>
            {unit.traits.map(t => <ListItemText
                key={t.id}
                primary={t.name}
                primaryTypographyProps={{ sx: { fontSize: 10, marginLeft: 2, } }}
                secondary={t.description}
                secondaryTypographyProps={{ sx: { fontSize: 10, marginLeft: 2, } }}
            />)}
        </Stack>}
        {unit.enhancements && unit.enhancements.length > 0 && <Stack sx={{ marginTop: 2 }}>
            <Typography sx={{
                fontSize: 12, color: RP_COLOR
            }}>Miglioramenti</Typography>
            {unit.enhancements.map(t => <ListItemText
                key={t.id}
                primary={t.name}
                primaryTypographyProps={{ sx: { fontSize: 10, marginLeft: 2, } }}
                secondary={t.description}
                secondaryTypographyProps={{ sx: { fontSize: 10, marginLeft: 2, } }}
            />)}

        </Stack>}
        {unit.relics && unit.relics.length > 0 && <Stack sx={{ marginTop: 2 }}>
            <Typography sx={{
                fontSize: 12, color: RP_COLOR
            }}>Reliquie</Typography>
            {unit.relics.map(t => <ListItemText
                key={t.id}
                primary={t.name}
                primaryTypographyProps={{ sx: { fontSize: 10, marginLeft: 2, } }}
                secondary={t.description}
                secondaryTypographyProps={{ sx: { fontSize: 10, marginLeft: 2, } }}
            />)}

        </Stack>}
        {unit.scars && unit.scars.length > 0 && <Stack sx={{ marginTop: 2 }}>
            <Typography sx={{
                fontSize: 12, color: WARN_COLOR
            }}>Ferite di guerra</Typography>
            {unit.scars.map(t => <ListItemText
                key={t.id}
                primary={t.name}
                primaryTypographyProps={{ sx: { fontSize: 10, marginLeft: 2, } }}
                secondary={t.description}
                secondaryTypographyProps={{ sx: { fontSize: 10, marginLeft: 2, } }}
            />)}

        </Stack>}

        {ext.unitUpgrades(unit)}

    </Stack>
}

export function CUnitKwywordEditor(props) {
    const { keywords, call, onComplete, ...rest } = props;
    const [edit, setEdit] = useState(keywords)

    return <UDialog {...rest}>
        <DialogContent>
            <Stack>
                {Object.keys(UNIT_CATEGORIES).map(c => <FormControlLabel
                    key={c}
                    onClick={() => {
                        if (edit.includes(c)) {
                            setEdit(edit.filter(e => e !== c))
                        } else {
                            setEdit([...edit, c])
                        }
                    }}
                    control={<Checkbox
                        checked={edit.includes(c)}
                    />} label={UNIT_CATEGORIES[c]} />)}
            </Stack>
        </DialogContent>
        <DialogActions>
            <UDialogClose>Annulla</UDialogClose>
            <UDialogCloseOnCompleteButton call={call} payload={edit} onComplete={onComplete} variant="contained">
                Salva
            </UDialogCloseOnCompleteButton>
        </DialogActions>
    </UDialog>
}

export function AddBattleTraitButton(props) {
    const unit = useUnit()
    const traits = useTraits(unit);

    const { name, desc, cmd } = props;
    const style = gradient(['#735228', '#F4C92F', '#735228'])
    const [req, setReq] = useState({ name: '', flavor: '', description: '' })
    const valid = req.name.length > 3 && req.description.length > 3;
    const btnConfirmStyle = valid ? style : {}
    const currents = unit.traits.map(t => t.name)

    if (traits.length == 0) {
        return <></>
    }

    return <UDialog button={<UButton
        variant="contained"
        fullWidth={true}
        className=""
        sx={{
            ...style,
            border: '2px solid ' + '#F4C92F',
            color: WHITE
        }}>
        {desc}
    </UButton>}>
        <DialogTitle>
            <ULine>
                {name}
                <UDialgoCloseIcon />
            </ULine>
        </DialogTitle>
        <DialogContent>
            <Stack spacing={2}>
                <Typography sx={{ fontSize: 12 }}>{desc}</Typography>
                {traits.map(t => <BattleTraitCollection currents={currents} req={req} setReq={setReq} coll={t} key={t.name} />)}
            </Stack>
        </DialogContent>
        <DialogActions>
            <UDialogCloseOnCompleteButton
                fullWidth
                onComplete={() => {
                    setReq({ name: '', flavor: '', description: '' });
                    unit.reload();
                }}
                payload={req}
                disabled={!valid}
                sx={btnConfirmStyle}
                variant="contained"
                call={cmd}>Aggiungi</UDialogCloseOnCompleteButton>
        </DialogActions>
    </UDialog>
}

export function CIconDice(props) {
    const { dice } = props;
    return <Box sx={{ lineHeight: 0, background: SECONDARY_COLOR, borderRadius: 3 }}>
        <UIcon name={"dice_" + dice} />
    </Box>
}

function BattleTraitCollection(props) {
    const ext = useExt()
    const { coll, req, setReq, currents, ...rest } = props;
    const dices = ["1", "2", "3", "4", "5", "6",]
    const traits = coll.dices

    return <Stack>
        <Typography variant="h5">{coll.name}</Typography>
        <List>
            {dices.map(d => <BattleTraitButton
                key={d}
                currents={currents}
                trait={traits[d]}
                req={req}
                setReq={setReq}
                icon={<CIconDice dice={d} />} />)}
            {ext.customTraits(props)}
        </List>
    </Stack>

}

export function BattleTraitButton(props) {
    const { icon, trait, req, setReq, currents, ...rest } = props;
    return <UListItemButton
        disabled={currents.includes(trait.name) || rest.disabled}
        selected={req.name === trait.name || currents.includes(trait.name)}
        onClick={() => setReq(trait)}>
        <ListItemIcon>
            {icon}
        </ListItemIcon>
        <ListItemText primary={trait.name} secondary={trait.description} />
    </UListItemButton>
}

export function AddWeaponModificationButton(props) {
    const unit = useUnit()
    const { name, desc, cmd } = props;
    const style = gradient(['#735228', '#F4C92F', '#735228'])
    const [req, setReq] = useState([])
    const [weapon, setWeapon] = useState('')
    const valid = req.length == 2
    const btnConfirmStyle = valid ? style : {}
    const dices = ["1", "2", "3", "4", "5", "6",]
    const selected = req.map(r => r.name)

    function switchSel(d) {
        const mod = WEAPON_MODIFICATIONS[d]
        if (selected.includes(mod.name)) {
            setReq(req.filter(r => r.name !== mod.name))
        } else {
            setReq([...req, mod])
        }
    }

    useEffect(() => {
        if (weapon.length < 3) {
            setReq([])
        }
    }, [weapon])

    return <UDialog button={<UButton
        variant="contained"
        fullWidth={true}
        className=""
        sx={{
            ...style,
            border: '2px solid ' + '#F4C92F',
            color: WHITE
        }}>
        {desc}
    </UButton>}>
        <DialogTitle>
            <ULine>
                {name}
                <UDialgoCloseIcon />
            </ULine>
        </DialogTitle>
        <DialogContent>
            <Stack spacing={2}>
                <Typography sx={{ fontSize: 12, paddingBottom: 4 }}>{desc}</Typography>
                <TextField label="Arma da modificare" size="small" value={weapon} onChange={e => setWeapon(e.target.value.trim())} />

                {dices.map(d => <UListItemButton
                    onClick={() => switchSel(d)}
                    selected={selected.includes(WEAPON_MODIFICATIONS[d].name)}
                    disabled={weapon.length < 3 || (selected.length >= 2 && !selected.includes(WEAPON_MODIFICATIONS[d].name))}
                    key={d}>
                    <ListItemIcon>
                        <CIconDice dice={d} />
                    </ListItemIcon>
                    <ListItemText
                        primary={WEAPON_MODIFICATIONS[d].name}
                        secondary={WEAPON_MODIFICATIONS[d].description}
                    />
                </UListItemButton>)}
            </Stack>
        </DialogContent>
        <DialogActions>
            <UDialogCloseOnCompleteButton
                fullWidth
                onComplete={() => {
                    setReq([]);
                    unit.reload();
                }}
                payload={{
                    weapon,
                    modifications: req
                }}
                disabled={!valid}
                sx={btnConfirmStyle}
                variant="contained"
                call={cmd}>Aggiungi</UDialogCloseOnCompleteButton>
        </DialogActions>
    </UDialog>
}

export function AddRelicButton(props) {
    const unit = useUnit()
    const { name, desc, cmd } = props;
    const style = gradient(['#735228', '#F4C92F', '#735228'])
    const [req, setReq] = useState(null)
    const relics = useRelics(unit)
    const valid = Boolean(req)
    const btnConfirmStyle = valid ? style : {}

    return <UDialog fullWidth button={<UButton
        variant="contained"
        fullWidth={true}
        className=""
        sx={{
            ...style,
            border: '2px solid ' + '#F4C92F',
            color: WHITE
        }}>
        {desc}
    </UButton>}>
        <DialogTitle>
            <ULine>
                {name}
                <UDialgoCloseIcon />
            </ULine>
        </DialogTitle>
        <DialogContent>
            <Stack spacing={2}>
                <Typography sx={{ fontSize: 12 }}>{desc}</Typography>
            </Stack>

            <List>
                {Object.keys(relics).map(type => <CrusadeRelicGroup
                    key={type}
                    type={type}
                    relics={relics[type]}
                    onSelect={setReq}
                    selected={req}
                />)}
            </List>
        </DialogContent>
        <DialogActions>
            <UDialogCloseOnCompleteButton
                fullWidth
                onComplete={unit.reload}
                payload={req}
                disabled={!valid}
                sx={btnConfirmStyle}
                variant="contained"
                call={cmd}>Aggiungi</UDialogCloseOnCompleteButton>
        </DialogActions>
    </UDialog>
}
const COMMON_RELICS = ['artificer', 'antiquity', 'legendary',]

function CrusadeRelicGroup(props) {
    const { relics, type, onSelect, selected } = props;

    return <>
        <ListItem>
            <Typography variant="h4" sx={{ color: RP_COLOR }}>{type}</Typography>
        </ListItem>
        {relics.map(r => <CrusadeRelic selected={selected && selected.name === r.name} onClick={() => onSelect({
            ...r,
            type: COMMON_RELICS.includes(type) ? type : 'custom',
        })} type={type} relic={r} key={r.name} />)}
    </>
}


function CrusadeRelic(props) {
    const ext = useExt()
    const unit = useUnit()
    const { relic, type, ...rest } = props;
    const disabled = unit.relics.map(r => r.name.toLowerCase()).includes(relic.name.toLowerCase())

    if (!COMMON_RELICS.includes(type)) {
        return ext.customRelics({
            relic, type, disabled, ...rest
        })
    }

    return <UListItemButton {...rest} disabled={disabled}>
        <Stack>
            <Typography sx={{ fontWeight: 600 }}>{relic.name}</Typography>
            <Typography sx={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{relic.description}</Typography>
        </Stack>
    </UListItemButton>
}

export function CUnitEditUnitUpgradesButton(props) {
    return <CUnitEditComposition
        disableEditModels={true}
        type="composition.upgrade.edit"
        {...props}
    >Modifica equipaggiamento</CUnitEditComposition>
}

export function CUnitEditUnitModelButton(props) {
    const unit = useUnit()
    const multiModel = unit.composition.models.length > 0

    if (!multiModel) {
        return <></>
    }

    return <CUnitEditComposition
        disableEditUpgrades={true}
        type="composition.models.edit"
        {...props}
    >Modifica composizione</CUnitEditComposition>
}

function CUnitEditComposition(props) {
    const { onComplete, type, disableEditModels, disableEditUpgrades, ...rest } = props;
    const unit = useUnit()
    const multiModel = unit.composition.models.length > 0
    const currComposition = {
        ...unit.composition,
        models: multiModel ? [...unit.composition.models] : null,
    }
    const [edit, setEdit] = useState(currComposition)

    return <UDialog fullScreen {...rest} button={<UButton>{props.children}</UButton>}>
        <DialogTitle>
            <ULine>
                Modifica {unit.name || unit.unitType}
                <UDialgoCloseIcon />
            </ULine>
        </DialogTitle>
        <DialogContent>
            <UnitComposition
                disableEditModels={disableEditModels}
                disableEditUpgrades={disableEditUpgrades}
                unit={{
                    name: unit.name || unit.unitType,
                    composition: currComposition
                }} onChange={setEdit}>
                <CUnitEditor />
            </UnitComposition>
        </DialogContent>
        <DialogActions>
            <PayButtonCloseOnComplete onComplete={onComplete} type={type} unitId={unit.id} params={[JSON.stringify(edit)]} />
        </DialogActions>
    </UDialog>
}

function PayButtonCloseOnComplete(props) {
    const dialog = useDialog()
    return <CPayButton {...props} onComplete={() => {
        dialog.close();
        props.onComplete();
    }} />
}

export function ExtUnitSection() {
    const ext = useExt();

    return ext.unitSections();
}

export function ExtUnitStats() {
    const ext = useExt();

    return ext.unitStats();
}